/**
 * Les emplacements publicitaires de Meta, en clair.
 *
 * ## Ce que l'API renvoie, et pourquoi c'est illisible
 *
 * Le tableau des placements croise deux breakdowns, `publisher_platform` et `platform_position`,
 * et le serveur les colle : `instagram_instagram_stories`, `facebook_feed`,
 * `instagram_explore_grid_home`. La répétition n'est pas une erreur — Meta préfixe lui-même
 * certaines positions du nom de leur plateforme, et pas d'autres.
 *
 * Le composant portait déjà une table de libellés, mais construite sur des clés que l'API ne
 * rend pas (`instagram_stories` là où elle rend `instagram_instagram_stories`) : aucune ligne ne
 * correspondait, et toutes retombaient sur la valeur brute. D'où le découpage d'abord, la
 * traduction ensuite : la plateforme se reconnaît par préfixe, la position par ce qui reste.
 *
 * ## Pourquoi une explication, et pas seulement un nom
 *
 * « Explore grid home » ne dit rien à qui n'a pas la nomenclature de Meta en tête, et deux
 * emplacements voisins n'ont pas du tout la même valeur publicitaire : une story plein écran et
 * une colonne de droite ne se comparent pas. La phrase dit **où la publicité apparaît**, ce qui
 * est la seule chose dont on a besoin pour lire une ligne du tableau.
 */

/**
 * Les plateformes de diffusion, du préfixe le plus long au plus court — voir `decouper`.
 *
 * Le `libelle` est aussi la clé que lit `LogoPlateforme` : les deux doivent rester accordés,
 * faute de quoi le pictogramme retombe sur le point d'interrogation. C'est voulu plutôt que
 * contraignant — une marque affichée au hasard serait pire qu'un point d'interrogation.
 */
export const PLATEFORMES = {
  audience_network: { libelle: 'Audience Network' },
  facebook: { libelle: 'Facebook' },
  instagram: { libelle: 'Instagram' },
  messenger: { libelle: 'Messenger' },
  threads: { libelle: 'Threads' },
  unknown: { libelle: 'Non précisé' }
};

/**
 * Les positions, telles que `platform_position` les nomme.
 *
 * Les doublons apparents — `story` et `instagram_stories`, `search` et `instagram_search` — sont
 * les deux écritures que Meta emploie réellement selon les comptes et les périodes. Les deux
 * sont donc prévues plutôt que normalisées : une normalisation nous ferait deviner, et une
 * position inconnue est mieux servie par son nom brut que par une correspondance approximative.
 */
export const POSITIONS = {
  // — Fils —
  feed: { libelle: 'Fil d\'actualité', explication: 'Dans le fil principal, entre les publications.' },
  stream: { libelle: 'Fil d\'actualité', explication: 'Dans le fil principal, entre les publications.' },
  profile_feed: { libelle: 'Fil de profil', explication: 'Dans le fil d\'un profil, entre les publications.' },
  facebook_profile_feed: { libelle: 'Fil de profil', explication: 'Dans le fil d\'un profil Facebook, entre les publications.' },
  instagram_profile_feed: { libelle: 'Fil de profil', explication: 'Dans le fil d\'un profil Instagram, entre les publications.' },
  groups: { libelle: 'Fil des groupes', explication: 'Dans le fil d\'un groupe Facebook.' },
  facebook_groups_feed: { libelle: 'Fil des groupes', explication: 'Dans le fil d\'un groupe Facebook.' },
  video_feeds: { libelle: 'Fil vidéo', explication: 'Entre les vidéos du fil dédié aux vidéos.' },
  biz_disco_feed: { libelle: 'Fil de découverte pro', explication: 'Dans le fil de découverte des contenus professionnels.' },
  marketplace: { libelle: 'Marketplace', explication: 'Dans le fil de Marketplace, parmi les annonces.' },

  // — Formats plein écran —
  story: { libelle: 'Stories', explication: 'En plein écran, intercalée entre deux stories.' },
  facebook_stories: { libelle: 'Stories', explication: 'En plein écran, intercalée entre deux stories Facebook.' },
  instagram_stories: { libelle: 'Stories', explication: 'En plein écran, intercalée entre deux stories Instagram.' },
  reels: { libelle: 'Reels', explication: 'En plein écran, intercalée entre deux Reels.' },
  facebook_reels: { libelle: 'Reels', explication: 'En plein écran, intercalée entre deux Reels Facebook.' },
  instagram_reels: { libelle: 'Reels', explication: 'En plein écran, intercalée entre deux Reels Instagram.' },
  facebook_reels_overlay: { libelle: 'Bandeau sur Reels', explication: 'Bandeau superposé à un Reel en cours de lecture.' },
  instagram_reels_overlay: { libelle: 'Bandeau sur Reels', explication: 'Bandeau superposé à un Reel en cours de lecture.' },

  // — Découverte et recherche —
  explore: { libelle: 'Explorer', explication: 'Entre les publications de l\'onglet Explorer.' },
  instagram_explore: { libelle: 'Explorer', explication: 'Entre les publications de l\'onglet Explorer.' },
  instagram_explore_grid_home: { libelle: 'Explorer (grille)', explication: 'Dans la mosaïque de l\'onglet Explorer, avant d\'ouvrir une publication.' },
  search: { libelle: 'Recherche', explication: 'Dans les résultats de recherche.' },
  instagram_search: { libelle: 'Recherche', explication: 'Dans les résultats de recherche Instagram.' },

  // — Vidéo —
  instream_video: { libelle: 'Vidéo in-stream', explication: 'Pendant la lecture d\'une vidéo, comme une coupure publicitaire.' },
  facebook_instream_video: { libelle: 'Vidéo in-stream', explication: 'Pendant la lecture d\'une vidéo, comme une coupure publicitaire.' },
  igtv: { libelle: 'IGTV', explication: 'Avant ou pendant une vidéo longue Instagram.' },
  rewarded_video: { libelle: 'Vidéo récompensée', explication: 'Vidéo qu\'une personne choisit de regarder contre une récompense dans une application.' },

  // — Autres —
  right_hand_column: { libelle: 'Colonne de droite', explication: 'Encart à droite du fil, sur ordinateur uniquement.' },
  instant_article: { libelle: 'Articles instantanés', explication: 'À l\'intérieur d\'un article instantané ouvert depuis Facebook.' },
  // « AN » pour Audience Network : c'est la forme que l'API emploie réellement, `classic` seul
  // étant la forme historique. Les deux sont prévues.
  an_classic: { libelle: 'Applications partenaires', explication: 'Bannière ou encart dans une application tierce du réseau Audience Network.' },
  classic: { libelle: 'Applications partenaires', explication: 'Bannière ou encart dans une application tierce du réseau Audience Network.' },
  messenger_home: { libelle: 'Accueil Messenger', explication: 'Dans l\'onglet Discussions de Messenger, entre les conversations.' },
  sponsored_messages: { libelle: 'Messages sponsorisés', explication: 'Message envoyé dans une conversation déjà engagée avec la marque.' },

  unknown: { libelle: 'Non précisé', explication: 'Meta n\'a pas indiqué l\'emplacement de ces impressions.' }
};

/**
 * Sépare `publisher_platform` et `platform_position` dans la valeur collée par le serveur.
 *
 * On cherche le **plus long** préfixe connu : sans cela, `audience_network_classic` se lirait
 * comme la plateforme `audience` suivie de la position `network_classic`.
 */
function decouper(valeur) {
  const brut = String(valeur || 'unknown');

  const prefixe = Object.keys(PLATEFORMES)
    .filter(p => brut === p || brut.startsWith(`${p}_`))
    .sort((a, b) => b.length - a.length)[0];

  if (!prefixe) return { plateforme: null, position: brut };
  return { plateforme: prefixe, position: brut.slice(prefixe.length + 1) || prefixe };
}

/**
 * « instagram_explore_grid_home » → « Explore grid home », à défaut de mieux.
 *
 * Le nom de la plateforme est retiré quand la position le répète : l'affichage le place déjà
 * devant, et « Facebook — Facebook notification » le disait deux fois.
 */
function enClair(position, plateforme) {
  const sansPrefixe = plateforme && position.startsWith(`${plateforme}_`)
    ? position.slice(plateforme.length + 1)
    : position;

  return sansPrefixe
    .split('_')
    .join(' ')
    .replace(/^./, c => c.toUpperCase());
}

/**
 * Décrit un emplacement : son libellé affichable, et la phrase qui dit où la publicité apparaît.
 *
 * Une position inconnue garde son nom brut remis en forme, et le dit — mieux vaut un intitulé
 * approximatif signalé comme tel qu'une correspondance inventée.
 *
 * @returns {{libelle: string, plateforme: string, explication: string, brut: string, connu: boolean}}
 */
export function decrirePlacement(valeur) {
  const brut = String(valeur || 'unknown');
  const { plateforme, position } = decouper(brut);

  const infosPlateforme = PLATEFORMES[plateforme] || PLATEFORMES.unknown;
  const infosPosition = POSITIONS[position];

  const nomPlateforme = plateforme ? infosPlateforme.libelle : null;
  const libellePosition = infosPosition ? infosPosition.libelle : enClair(position, plateforme);

  return {
    // Pas de « Non précisé — Non précisé » : quand les deux moitiés disent la même chose,
    // une seule suffit.
    libelle: nomPlateforme && nomPlateforme !== libellePosition
      ? `${nomPlateforme} — ${libellePosition}`
      : libellePosition,
    // Le nom de la plateforme sert aussi de clé au pictogramme : voir `LogoPlateforme`.
    plateforme: nomPlateforme || 'Non précisé',
    explication: infosPosition
      ? infosPosition.explication
      : 'Emplacement non répertorié : Meta en a ajouté un depuis la dernière mise à jour de cette liste.',
    brut,
    connu: Boolean(infosPosition)
  };
}

export default decrirePlacement;
